import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import {
  FlowContainer,
  FlowNode,
  FlowArrow,
  FlowTerminal,
  FlowNote,
} from '../FlowPrimitives';

// ============================================================================
// Experiment 3 - Spectrophotometric Determination of Phosphate in Cola
// Cross-referenced with CHEM2131 Lab Manual, Experiment 3
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp3',
  title: 'Spectrophotometric Determination of Phosphate in Cola Beverages',
  objectives: [
    'Prepare calibration standards for spectrophotometric analysis',
    'Use a UV-Vis spectrophotometer to measure absorbance at 400 nm',
    'Construct a calibration curve and determine unknown phosphate concentration',
    'Apply the Beer-Lambert Law to quantitative analysis',
  ],
  safetyNotes: [
    'Vanadomolybdate reagent contains sulfuric acid - handle with care, avoid skin contact',
    'Hot plate presents burn hazard - do not touch heating surfaces',
    'Handle cuvettes carefully - they are fragile and expensive',
  ],
  equipment: [
    'UV-Vis Spectrophotometer',
    'Matched cuvettes',
    'Volumetric flasks (100 mL)',
    'Pipettes (various sizes)',
    'Hot plate',
    '250 mL beaker',
  ],
  chemicals: [
    'Phosphate stock solution (1.0 x 10^-3 M from 0.1360 g KH₂PO₄ in 1 L)',
    'Vanadomolybdate reagent',
    'Cola beverage sample',
    'Distilled water',
  ],
  procedure: [
    {
      number: 1,
      title: 'Part A: Sample Preparation (Decarbonation)',
      details: [
        'Measure approximately 100 mL of cola into a 250 mL beaker',
        'Cover with a watch glass to prevent loss',
        'Heat gently on hot plate for 10 minutes to remove CO₂',
        'Cool to room temperature',
        'Prepare a 50-fold dilution: pipette appropriate volume, add 0.5 mL conc H₂SO₄',
        'Dilute to mark in 100 mL volumetric flask and mix thoroughly',
      ],
      safetyNote: 'Hot plate burn hazard - use beaker tongs when moving heated glassware',
      manualRef: 'Lab Manual - Part A',
    },
    {
      number: 2,
      title: 'Part B: Standard Preparation',
      details: [
        'Prepare stock: dissolve 0.1360 g KH₂PO₄ in 1 L (1.0 x 10^-3 M)',
        'Prepare calibration standards by dilution in 100 mL volumetric flasks',
        'Blank: 0 mL stock (0 M)',
        'Std 1: 2.0 x 10^-5 M',
        'Std 2: 5.0 x 10^-5 M',
        'Std 3: 7.5 x 10^-4 M',
      ],
      manualRef: 'Lab Manual - Part B',
    },
    {
      number: 3,
      title: 'Part C: Color Development',
      details: [
        'To each standard/sample: add 10 mL vanadomolybdate reagent',
        'Then add 5 mL of standard/sample solution',
        'Dilute to the mark in 100 mL volumetric flask',
        'Mix thoroughly',
        'Wait 10 minutes for complete yellow color development',
      ],
      safetyNote: 'Vanadomolybdate reagent contains sulfuric acid',
      manualRef: 'Lab Manual - Part C',
    },
    {
      number: 4,
      title: 'Part D: Spectrophotometric Measurement',
      details: [
        'Set the spectrophotometer wavelength to 400 nm',
        'Zero the instrument using the blank solution',
        'Measure and record absorbance of each calibration standard',
        'Measure and record absorbance of the cola sample',
      ],
      manualRef: 'Lab Manual - Part D',
    },
    {
      number: 5,
      title: 'Calculations',
      details: [
        'Plot absorbance (y-axis) vs. concentration (x-axis) for standards',
        'Draw best-fit line through the data points',
        'Determine slope (m) and intercept (b) from A = mc + b',
        'Calculate R-squared for the regression',
        'Use calibration curve to find unknown cola phosphate concentration',
        'Account for 50-fold dilution factor to determine original cola concentration',
      ],
      manualRef: 'Lab Manual - Calculations',
    },
  ],
  keyFormulas: [
    'A = abc  [Beer-Lambert Law]',
    'A = absorbance, a = absorptivity coefficient, b = path length (cm), c = concentration',
    'A = mc + b  [Calibration curve linear equation]',
    'C_original = C_measured × dilution factor',
    '% Error = |Experimental - Accepted| / Accepted × 100',
  ],
  tips: [
    'Ensure complete decarbonation of cola - residual CO₂ causes bubbles in the cuvette and erratic readings',
    'Always handle cuvettes by the frosted sides, never the clear optical faces',
    'Wipe cuvettes with lint-free tissue before placing in spectrophotometer',
    'Wait the full 10 minutes for color development - incomplete reaction gives low absorbance',
    'The calibration curve should be linear with R-squared close to 1.0 (ideally > 0.99)',
    'If sample absorbance exceeds the highest standard, dilute the sample and re-measure',
  ],
};

// ============================================================================
// Flowchart: Phosphate in Cola Procedure
// Layout: vertical flow, 600px wide, nodes centered at x=210 (width 180)
// ============================================================================

function Flowchart() {
  // Horizontal center for nodes: (600 - 180) / 2 = 210
  const cx = 210;
  const nw = 180;
  const nh = 56;
  const tw = 140;
  // Terminal center: (600 - 140) / 2 = 230
  const tx = 230;

  return (
    <FlowContainer width={600} height={740} title="Phosphate in Cola Procedure Flow">
      {/* Start */}
      <FlowTerminal x={tx} y={40} label="Start" color="#6366f1" />
      <FlowArrow from={{ x: 300, y: 76 }} to={{ x: 300, y: 96 }} />

      {/* Step 1: Sample Prep / Decarbonation */}
      <FlowNode x={cx} y={96} title="Decarbonate Cola" subtitle="Heat ~100 mL for 10 min (watch glass)" color="#0d9488" />
      <FlowNote x={420} y={100} text="Hot plate hazard!" width={120} color="#ef4444" />
      <FlowArrow from={{ x: 300, y: 152 }} to={{ x: 300, y: 172 }} />

      {/* Step 2: Dilute sample */}
      <FlowNode x={cx} y={172} title="Dilute Sample 50-fold" subtitle="+ 0.5 mL conc H₂SO₄, to 100 mL" color="#0d9488" />
      <FlowArrow from={{ x: 300, y: 228 }} to={{ x: 300, y: 248 }} />

      {/* Step 3: Prepare Standards */}
      <FlowNode x={cx} y={248} title="Prepare Standards" subtitle="Molar concs from 1.0x10^-3 M stock" color="#2563eb" />
      <FlowNote x={420} y={252} text="5 standards + blank" width={120} color="#f59e0b" />
      <FlowArrow from={{ x: 300, y: 304 }} to={{ x: 300, y: 324 }} />

      {/* Step 4: Color Development */}
      <FlowNode x={cx} y={324} title="Color Development" subtitle="Add vanadomolybdate, wait 10 min" color="#2563eb" />
      <FlowNote x={420} y={328} text="Contains H₂SO₄!" width={120} color="#ef4444" />
      <FlowArrow from={{ x: 300, y: 380 }} to={{ x: 300, y: 400 }} />

      {/* Step 5: Set Wavelength & Zero */}
      <FlowNode x={cx} y={400} title="Set Spectrophotometer" subtitle="Set 400 nm, zero with blank" color="#7c3aed" />
      <FlowArrow from={{ x: 300, y: 456 }} to={{ x: 300, y: 476 }} />

      {/* Step 6: Measure Absorbance */}
      <FlowNode x={cx} y={476} title="Measure Absorbance" subtitle="Standards then cola sample" color="#7c3aed" />
      <FlowArrow from={{ x: 300, y: 532 }} to={{ x: 300, y: 552 }} />

      {/* Step 7: Build Calibration Curve */}
      <FlowNode x={cx} y={552} title="Build Cal. Curve" subtitle="Plot A vs. C, find best-fit line" color="#0d9488" />
      <FlowNote x={420} y={556} text="R-squared > 0.99" width={120} color="#f59e0b" />
      <FlowArrow from={{ x: 300, y: 608 }} to={{ x: 300, y: 628 }} />

      {/* Step 8: Calculate Concentration */}
      <FlowNode x={cx} y={628} title="Calculate [PO4]" subtitle="Use cal. curve + dilution factor" color="#0d9488" />
      <FlowArrow from={{ x: 300, y: 684 }} to={{ x: 300, y: 700 }} />

      {/* End */}
      <FlowTerminal x={tx} y={700} label="End" color="#6366f1" />
    </FlowContainer>
  );
}

// ============================================================================
// Export
// ============================================================================

export default function Exp3Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
